import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import deleteIcon_white from '../../../../assets/images/svg-images/icon-delete-white.svg';
import edit_white from '../../../../assets/images/svg-images/icon-edit-white.svg';
import warning from '../../../../assets/images/svg-images/icon-warning.svg';
import publish from '../../../../assets/images/svg-images/icon-upload.svg';

import useDeleteApi from '../../../../Hooks/useDeleteApi';
import { PrivilegeTypes } from '../../../../Common/UserPrivileges';
import useCheckPermission from '../../../../Hooks/useCheckPermission';
import ConfirmActionModal from '../../../../Components/Modal/ConfirmActionModal';
import Loader from '../../../../Components/Loader/loader';
import messageConfigs from '../../../../Helper/PopupMessageConfig';
import useChangePublishedStatusApi from '../../../../Hooks/useChangePublishedStatusApi';
import useFetchDataApi from '../../../../Hooks/useFetchDataApi';
import { ToolTipConfig } from '../../../../Common/ToolTipConfig';
import AdminDashboardSelectionDropDown from '../../../../Components/Admin/AdminDashboardSelectionDropDown';
import ActionDropdown from '../../../../Components/Admin/ActionDropdown';

const SUPER_ADMIN_ID = 4;

export default function AllVideos({ history }) {
  const { t } = useTranslation();
  const canEditVideo = useCheckPermission(
    PrivilegeTypes.VIDEO.EDIT_VIDEO_ON_MASTER
  );
  const canDeleteVideo = useCheckPermission(
    PrivilegeTypes.VIDEO.DELETE_VIDEO_ON_MASTER
  );
  const canSuspendPublishVideo = useCheckPermission(
    PrivilegeTypes.VIDEO.SUSPEND_PUBLISH_VIDEO_ON_MASTER
  );

  const [isGridView, setIsGridView] = useState(true);
  const [searchVideoName, setSearchVideoName] = useState('');
  const [organizedVidoes, setOrganizedVidoes] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] = useState(
    null
  );
  const { roleList } = useSelector(state => state?.auth?.userData);
  const {
    suspendVideoLoading,
    fetchAllVideosByGroupData,
    fetchAllVideosByGroupLoading,
    deleteVideoLoading,
  } = useSelector(state => state?.video);
  const { themeInfo } = useSelector(state => state?.auth);
  const defaultVideo = themeInfo?.defaultVideoImageUrl;

  const [updateIsPublishedStatus] = useChangePublishedStatusApi();
  const [fetchData] = useFetchDataApi();
  const [deleteVideo] = useDeleteApi();

  useEffect(() => {
    if (roleList && roleList.length > 0) {
      setIsSuperAdmin(
        [...roleList].map(({ id }) => id).includes(SUPER_ADMIN_ID)
      );
    }
  }, [roleList]);

  useEffect(() => {
    getAllVideos();
  }, []);

  const getAllVideos = () => fetchData({ type: 'VIDEO' });

  useEffect(() => {
    if (fetchAllVideosByGroupData) {
      const organizedVideos = fetchAllVideosByGroupData.map(video => {
        return {
          ...video,
          name: video.title,
          imageUrl: video.previewImgUrl || defaultVideo,
          bannerOrFeatured: video.isBanner ? 'banner' : 'featured',
        };
      });

      setOrganizedVidoes(organizedVideos);
    }
  }, [fetchAllVideosByGroupData]);

  useEffect(() => {
    if (!deleteVideoLoading && isDeleteConfirmModalOpen) {
      setIsDeleteConfirmModalOpen(null);
    }
  }, [deleteVideoLoading]);

  const hanldeIsPublishedStatus = videoId => {
    updateIsPublishedStatus({
      _id: videoId,
      type: 'VIDEO',
    });
  };

  const setVideoListTableActions = (value, video) => {
    switch (value) {
      case 'Suspend':
        hanldeIsPublishedStatus(video._id);
        break;

      case 'Publish':
        hanldeIsPublishedStatus(video._id);
        break;

      case 'Delete':
        handleDelete(video._id);
        break;

      case 'Edit':
        if (video.isNativeBannerAndFeatured) {
          history.push(`/edit-video/${video.bannerOrFeatured}/${video._id}`);
        } else {
          history.push(`/edit-video/${video._id}`);
        }
        break;

      default:
        break;
    }
  };

  const handleDelete = id => {
    let deleteRequestData = {
      vid_doc_standalone_Id: id,
      type: 'VIDEO',
      viewType: 'ALL_VIDEOS',
    };

    setIsDeleteConfirmModalOpen(deleteRequestData);
  };

  const renderAllVideosView = () => {
    return organizedVidoes
      .filter(
        ({ name }) =>
          searchVideoName === '' ||
          name?.toUpperCase().includes(searchVideoName.toUpperCase())
      )
      .map((video, index) => {
        if (isGridView) {
          return renderGridTile(video, index);
        } else {
          return renderTableRow(video, index);
        }
      });
  };

  const renderGridTile = (gridObject, index) => {
    const {
      _id,
      name,
      imageUrl,
      groupIds,
      isPublished,
      bannerOrFeatured,
      isNativeBannerAndFeatured,
    } = gridObject;

    const isHaveEditAccess = groupIds.some(id => {
      return canEditVideo[id];
    });

    const isHaveSuspendPublishAccess = groupIds.some(id => {
      return canSuspendPublishVideo[id];
    });

    const isHaveDeleteAccess = groupIds.some(id => {
      return canDeleteVideo[id];
    });

    return (
      <div className="reel-view--grid-video-wrapper" key={index}>
        <div
          className={`reel-view--grid-video ${
            !isPublished ? 'unpublished' : ''
          }`}
        >
          <div
            className="reel-view--grid-video-unpublished"
            onClick={() => history.push(`/theatre/${_id}`)}
          ></div>
          <img
            src={imageUrl}
            alt="reel-thumbnail"
            onError={event => {
              event.target.onerror = null;
              event.target.src = defaultVideo;
            }}
            onClick={() => history.push(`/theatre/${_id}`)}
            className="reel-thumbnail"
          />
          <div className="reel-view--grid-video-settings">
            {(isSuperAdmin || isHaveEditAccess) && (
              <img
                src={edit_white}
                alt="edit-icon"
                className="icon--edit-white"
                onClick={
                  isNativeBannerAndFeatured
                    ? () =>
                        history.push(`/edit-video/${bannerOrFeatured}/${_id}`)
                    : () => history.push(`/edit-video/${_id}`)
                }
              />
            )}
            {(isSuperAdmin || isHaveSuspendPublishAccess) && (
              <img
                src={isPublished ? warning : publish}
                alt={isPublished ? 'warning-icon' : 'publish-icon'}
                className={isPublished ? 'icon--warning' : 'icon--publish'}
                onClick={() => hanldeIsPublishedStatus(_id)}
              />
            )}
            {(isSuperAdmin || isHaveDeleteAccess) && (
              <img
                src={deleteIcon_white}
                alt="delete-icon"
                className="icon--delete-white"
                onClick={() => handleDelete(_id)}
              />
            )}
          </div>
        </div>
        <p>{name}</p>
      </div>
    );
  };

  const renderTableRow = (rowItem, index) => {
    const {
      date,
      name,
      imageUrl,
      groupIds,
      isPublished,
      noOfReels,
      views,
    } = rowItem;

    const isHaveEditAccess = groupIds.some(id => {
      return canEditVideo[id];
    });

    const isHaveSuspendPublishAccess = groupIds.some(id => {
      return canSuspendPublishVideo[id];
    });

    const isHaveDeleteAccess = groupIds.some(id => {
      return canDeleteVideo[id];
    });

    return (
      <div key={index} className="reel-view--table-row">
        <div className="reel-view--table-cell">
          <img
            src={imageUrl}
            className="reel-view--table-backgroundimg"
            onError={event => {
              event.target.onerror = null;
              event.target.src = defaultVideo;
            }}
          />
        </div>
        <div className="reel-view--table-cell">
          <p>{name}</p>
        </div>
        <div className="reel-view--table-cell">
          <p>{date ? moment.utc(date).local().format('DD/MM/YYYY') : '-'}</p>
        </div>
        <div className="reel-view--table-cell">
          <p>{groupIds ? groupIds.length : '-'}</p>
        </div>
        <div className="reel-view--table-cell">
          <p>{noOfReels}</p>
        </div>
        <div className="reel-view--table-cell">
          <p>{views || '0'}</p>
        </div>
        <div className="reel-view--table-cell">
          {(isSuperAdmin ||
            isHaveSuspendPublishAccess ||
            isHaveDeleteAccess ||
            isHaveEditAccess) && (
            <ActionDropdown
              ishaveEdit={isSuperAdmin || isHaveEditAccess}
              ishaveSuspend={
                (isSuperAdmin || isHaveSuspendPublishAccess) && isPublished
              }
              ishavePublish={
                (isSuperAdmin || isHaveSuspendPublishAccess) && !isPublished
              }
              ishaveDelete={isSuperAdmin || isHaveDeleteAccess}
              handleOnchange={option =>
                setVideoListTableActions(option.value, rowItem)
              }
            />
          )}
        </div>
      </div>
    );
  };

  const isPageLoading = fetchAllVideosByGroupLoading || suspendVideoLoading;

  return (
    <div className="all-users-table allreels allvideos ">
      <div
        className={`container loading-screen-parent ${
          isPageLoading ? 'setheight' : ''
        }`}
      >
        {isPageLoading ? (
          <Loader />
        ) : (
          <div className="all-users-table--wrapper">
            <div className="all-users-table--page-title">
              <div className="all-users-table--page-selection">
                <AdminDashboardSelectionDropDown
                  history={history}
                  selected={'VIDEOS'}
                />
              </div>
              <div className="all-users-table--page-settings">
                <input
                  className="form-input form-input--search"
                  type="search"
                  id="search"
                  name="search"
                  placeholder={t('Search')}
                  value={searchVideoName}
                  onChange={event => setSearchVideoName(event.target.value)}
                />
                {/* <Dropdown title="Filter By" options={[]} /> */}
                <button
                  className="btn btn--primary btn--createnew"
                  onClick={() => history.push('/create-video')}
                >
                  {t('Create Video')}
                </button>
                <div
                  className={`all-users-table--image grid ${
                    isGridView ? 'active' : ''
                  }`}
                  title={t(ToolTipConfig.general.gridView)}
                  onClick={() => setIsGridView(true)}
                />
                <div
                  className={`all-users-table--image list ${
                    !isGridView ? 'active' : ''
                  }`}
                  title={t(ToolTipConfig.general.tableView)}
                  onClick={() => setIsGridView(false)}
                />
              </div>
            </div>
            {isGridView ? (
              <div className="all-users-table--grid">
                <div className="all-users-table--grid-wrapper">
                  {renderAllVideosView()}
                </div>
              </div>
            ) : (
              <div className="all-users-table--table">
                <div className="all-users-table--table-header">
                  <div className="reel-view--table-cell">
                    <h5>{t('Preview')}</h5>
                  </div>
                  <div className="reel-view--table-cell">
                    <h5>{t('Title')}</h5>
                  </div>
                  <div className="reel-view--table-cell">
                    <h5>{t('Date Created')}</h5>
                  </div>
                  <div className="reel-view--table-cell">
                    <h5>{t('No. of Groups')}</h5>
                  </div>
                  <div className="reel-view--table-cell">
                    <h5>{t('No. of Reels')}</h5>
                  </div>
                  <div className="reel-view--table-cell">
                    <h5>{t('Total Views')}</h5>
                  </div>
                  <div className="reel-view--table-cell">
                    {/* Dropdown column */}
                  </div>
                </div>
                <div className="all-users-table--table-body">
                  {renderAllVideosView()}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {isDeleteConfirmModalOpen && (
        <ConfirmActionModal
          title={messageConfigs.deleteConfirm.title}
          message={messageConfigs.deleteConfirm.video}
          handleSuccess={() => deleteVideo(isDeleteConfirmModalOpen)}
          handleClose={() => setIsDeleteConfirmModalOpen(null)}
          isLoading={deleteVideoLoading}
        />
      )}
    </div>
  );
}
